import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PanelLeft, PanelRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sidebar, SidebarContent, SidebarHeader, SidebarInset, SidebarProvider, SidebarRail, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { useHotkey } from "@/lib/hooks/useHotkey";

/**
 * Rahmen aller Büro-Ansichten — Bearbeiter (`/admin/*`) wie Mandanten-Mitglied
 * (`/app/*`): links die Navigation, in der Mitte der Inhalt, rechts eine
 * Kontextleiste. Löst die drei Kopfzeilen-Rahmen ab, die vorher nebeneinander
 * standen (AdminLayout, MemberPageShell, die Reiterzeile in TenantLayout).
 *
 * **Nicht** für die Fahreransicht: `/app/heute` läuft auf 375px, dort ist eine
 * Leiste falsch. Die behält MobilePageShell.tsx. Ebenso wenig für den
 * Onboarding-Wizard, der seinen eigenen Rahmen mitbringt.
 *
 * Die rechte Leiste ist ein **Steckplatz, kein fester Inhalt**: ohne
 * `contextTitle` gibt es sie nicht, samt Schaltfläche und Tastengriff. Eine
 * Leiste, die auf jeder Seite dasselbe zeigte, wäre Zierrat — Kontext hat nicht
 * jede Ansicht.
 */

/**
 * Der Griff auf die **linke** Leiste, weitergereicht an die Kopfzeile.
 *
 * Nötig, weil zwei `SidebarProvider` ineinanderstecken: die Kopfzeile sitzt im
 * `SidebarInset` und damit innerhalb des inneren (rechten) Providers, ein
 * `useSidebar()` dort träfe also die falsche Leiste. Über `open`/`onOpenChange`
 * von außen ginge nur der Schreibtisch-Zustand — auf schmalen Schirmen schaltet
 * `toggleSidebar` das providereigene `openMobile`, an das von außen niemand
 * herankommt.
 */
const LeftSidebarContext = createContext<{ toggle: () => void; open: boolean } | null>(null);

function LeftSidebarBridge({ children }: { children: ReactNode }) {
  // Der nächste Provider über *dieser* Komponente ist der äußere; dass ihre
  // Kinder einen weiteren aufspannen, ändert daran nichts.
  const { toggleSidebar, open } = useSidebar();
  const value = useMemo(() => ({ toggle: toggleSidebar, open }), [toggleSidebar, open]);
  return <LeftSidebarContext.Provider value={value}>{children}</LeftSidebarContext.Provider>;
}

/**
 * Das Ziel der rechten Leiste. Ein Portal und nicht ein `context`-Prop, weil der
 * Inhalt fast immer am Zustand der Seite hängt: das Dispoboard füllt die Leiste
 * mit seinen unzugewiesenen Aufträgen, die dieselben Ziehgriffe und dieselbe
 * Auswahl tragen wie das Board selbst. Über ein Prop müsste dieser Zustand eine
 * Ebene höher wandern — der Steckplatz stünde dann im Weg, statt zu dienen.
 *
 * `null`, solange die Leiste nicht im Baum hängt: auf schmalen Schirmen ist sie
 * ein Sheet und existiert erst, wenn jemand sie öffnet.
 */
const ContextSlotContext = createContext<HTMLElement | null>(null);

/** Füllt die rechte Leiste des umgebenden SidebarShell. Ohne Leiste rendert sie nichts. */
export function ContextPanel({ children }: { children: ReactNode }) {
  const ziel = useContext(ContextSlotContext);
  return ziel ? createPortal(children, ziel) : null;
}

const SPEICHER_LINKS = "cleverfleet.leiste.links";
const SPEICHER_RECHTS = "cleverfleet.leiste.rechts";

/** Ein privates Fenster oder gesperrter Seitenspeicher darf die Leiste nicht unbedienbar machen — dann eben ohne Gedächtnis. */
function gemerkt(schluessel: string, vorgabe: boolean): boolean {
  try {
    const wert = window.localStorage.getItem(schluessel);
    return wert === null ? vorgabe : wert === "1";
  } catch {
    return vorgabe;
  }
}

function merken(schluessel: string, offen: boolean): void {
  try {
    window.localStorage.setItem(schluessel, offen ? "1" : "0");
  } catch {
    /* siehe gemerkt() */
  }
}

export interface SidebarShellProps {
  /** Überschrift der Ansicht, steht in der Kopfzeile und ist die <h1> der Seite. */
  title: string;
  /** Schaltflächen rechts in der Kopfzeile (Suche, Anlegen, Export …). */
  actions?: ReactNode;
  /**
   * Überschrift der rechten Leiste — und zugleich der Schalter dafür, dass es
   * sie überhaupt gibt. Wer ihn setzt, füllt sie mit `<ContextPanel>`.
   */
  contextTitle?: string;
  /** Breitenbegrenzung des Inhalts. Voreinstellung: keine — Board und Tabellen wollen den Platz. */
  maxWidth?: string;
  children: ReactNode;
}

export function SidebarShell({ title, actions, contextTitle, maxWidth, children }: SidebarShellProps) {
  const [leftOpen, setLeftOpen] = useState(() => gemerkt(SPEICHER_LINKS, true));
  const [rightOpen, setRightOpen] = useState(() => gemerkt(SPEICHER_RECHTS, true));
  // Zustand statt ref: das Portal darf erst zeichnen, wenn das Ziel steht, und
  // ein ref allein löst dafür kein zweites Rendern aus.
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  const changeLeft = useCallback((offen: boolean) => {
    setLeftOpen(offen);
    merken(SPEICHER_LINKS, offen);
  }, []);

  const changeRight = useCallback((offen: boolean) => {
    setRightOpen(offen);
    merken(SPEICHER_RECHTS, offen);
  }, []);

  /*
   * Cmd+B gehört der linken Leiste, Cmd+J der rechten. `SidebarProvider` hängt
   * sein eigenes Cmd+B ungefragt ans `window` — bei zwei Providern zweimal, und
   * eine Taste schaltete beide Leisten zugleich. `stopPropagation` hält das
   * Ereignis am `document` an, also im Blasenweg vor `window`; siehe
   * useHotkey.ts. Cmd+K bleibt der Befehlsleiste (AdminCommandMenu).
   */
  const toggleLeft = useCallback(() => changeLeft(!leftOpen), [changeLeft, leftOpen]);
  const toggleRight = useCallback(() => changeRight(!rightOpen), [changeRight, rightOpen]);
  useHotkey("b", toggleLeft, { meta: true, allowInInputs: true, stopPropagation: true });
  useHotkey("j", toggleRight, { meta: true, allowInInputs: true, stopPropagation: true, enabled: !!contextTitle });

  return (
    <SidebarProvider open={leftOpen} onOpenChange={changeLeft}>
      <AppSidebar />
      <LeftSidebarBridge>
        {/* w-auto/flex-1 statt des providereigenen w-full: der innere Provider ist hier ein Flex-Kind neben der linken Leiste, kein Wurzelrahmen. */}
        <SidebarProvider open={rightOpen} onOpenChange={changeRight} className="w-auto min-w-0 flex-1">
          <ContextSlotContext.Provider value={slot}>
            <SidebarInset id="main-content" tabIndex={-1} className="min-w-0">
              <ShellHeader title={title} actions={actions} contextTitle={contextTitle} />
              <div className={maxWidth ? `mx-auto w-full ${maxWidth} flex-1 px-6 py-8 max-lg:px-4` : "flex-1 p-6 max-lg:p-4"}>{children}</div>
            </SidebarInset>

            {/* role explizit: `Sidebar` rendert ein div, und ein aria-label an einem div ohne Rolle benennt nichts. */}
            {contextTitle ? (
              <Sidebar side="right" collapsible="offcanvas" role="complementary" aria-label={contextTitle}>
                <SidebarHeader className="h-(--header-h) shrink-0 justify-center border-b border-sidebar-border px-4">
                  <h2 className="text-sm font-semibold">{contextTitle}</h2>
                </SidebarHeader>
                <SidebarContent>
                  <div ref={setSlot} className="flex flex-col gap-2 p-2" />
                </SidebarContent>
                <SidebarRail aria-label={`${contextTitle} umschalten`} title={`${contextTitle} umschalten`} />
              </Sidebar>
            ) : null}
          </ContextSlotContext.Provider>
        </SidebarProvider>
      </LeftSidebarBridge>
    </SidebarProvider>
  );
}

/**
 * Die schmale Kopfzeile über dem Inhalt. Trägt beide Leisten-Schalter, weil eine
 * eingeklappte Leiste auf dem Telefon sonst keinen Weg zurück hätte — die
 * Ziehkanten (`SidebarRail`) gibt es dort nicht.
 */
function ShellHeader({ title, actions, contextTitle }: { title: string; actions?: ReactNode; contextTitle?: string }) {
  const left = useContext(LeftSidebarContext);
  const right = useSidebar();

  return (
    <header className="sticky top-0 z-header flex h-(--header-h) shrink-0 items-center gap-2 border-b border-border bg-card px-4">
      <Button variant="ghost" size="icon-sm" onClick={left?.toggle} aria-label="Navigation umschalten" aria-expanded={left?.open} title="Navigation umschalten (⌘B)">
        <PanelLeft aria-hidden="true" />
      </Button>
      {/* Mit derselben Variante, nicht als nacktes self-center: die Komponente setzt data-vertical:self-stretch, und ein Attributselektor sticht eine bloße Klasse — der 16px-Strich klebte sonst am oberen Rand. */}
      <Separator orientation="vertical" className="mr-1 h-4 data-vertical:self-center" />
      <h1 className="truncate text-base font-semibold">{title}</h1>

      <div className="ml-auto flex items-center gap-2">
        {actions}
        {contextTitle ? (
          <Button variant="ghost" size="icon-sm" onClick={right.toggleSidebar} aria-label={`${contextTitle} umschalten`} aria-expanded={right.open} title={`${contextTitle} umschalten (⌘J)`}>
            <PanelRight aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </header>
  );
}
